// # Ban and Suspend Modals Component
import React, { useState } from 'react';
import { functions } from '../../../../../auth/firebase';
import { httpsCallable } from 'firebase/functions';
import './ban_suspend.css';

// # Ban Modal Component
export const BanModal = ({ user, isOpen, onClose, onBanComplete }) => {
  // # State management
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  // # Handle ban user
  const handleBanUser = async () => {
    // # Validate confirmation text
    if (confirmText !== 'BAN') {
      setError('Please type BAN to confirm');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // # Call the ban user function
      const banUser = httpsCallable(functions, 'banUser');
      const result = await banUser({
        uid: user.uid,
        reason: reason || 'Violation of terms of service'
      });

      // # Handle success
      if (result.data.success) {
        onBanComplete(true);
        onClose();
      } else {
        setError(result.data.message || 'Failed to ban user');
      }
    } catch (error) {
      console.error('Error banning user:', error);
      setError(error.message || 'An error occurred while banning the user');
    } finally {
      setLoading(false);
    }
  };

  // # Handle unban user
  const handleUnbanUser = async () => {
    setLoading(true);
    setError(null);

    try {
      // # Call the unban user function
      const unbanUser = httpsCallable(functions, 'unbanUser');
      const result = await unbanUser({
        uid: user.uid
      });

      // # Handle success
      if (result.data.success) {
        onBanComplete(false);
        onClose();
      } else {
        setError(result.data.message || 'Failed to unban user');
      }
    } catch (error) {
      console.error('Error unbanning user:', error);
      setError(error.message || 'An error occurred while unbanning the user');
    } finally {
      setLoading(false);
    }
  };

  // # If modal is not open, don't render anything
  if (!isOpen) return null;

  // # Check if user is already banned
  const isBanned = user?.bans?.isBanned === true;

  return (
    <div className="bs_modal_overlay">
      <div className="bs_modal">
        <div className="bs_modal_header">
          <h2>{isBanned ? 'Unban User' : 'Ban User'}</h2>
          <button className="bs_close_button" onClick={onClose}>×</button>
        </div>

        <div className="bs_modal_content">
          {isBanned ? (
            // # Unban user content
            <div className="bs_unban_content">
              <p>
                This user is currently banned for the following reason:
              </p>
              <div className="bs_ban_reason">
                {user.bans.banReason || 'No reason provided'}
              </div>
              <p>
                Banned on: {new Date(user.bans.banTime?.toDate?.() || user.bans.banTime).toLocaleString()}
              </p>
              <p>
                Banned by: {user.bans.banBy || 'Admin'}
              </p>
              <p className="bs_warning">
                Are you sure you want to unban this user? They will regain access to the platform.
              </p>
            </div>
          ) : (
            // # Ban user content
            <div className="bs_ban_content">
              <p className="bs_warning">
                Warning: Banning a user will prevent them from accessing the platform until they are unbanned.
              </p>
              <div className="bs_form_group">
                <label>Reason for ban:</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Provide a reason for banning this user"
                  rows={4}
                />
              </div>
              <div className="bs_form_group">
                <label>Type "BAN" to confirm:</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type BAN to confirm"
                />
              </div>
            </div>
          )}

          {error && <div className="bs_error">{error}</div>}
        </div>

        <div className="bs_modal_footer">
          <button className="bs_cancel_button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          {isBanned ? (
            <button 
              className="bs_unban_button" 
              onClick={handleUnbanUser} 
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Unban User'}
            </button>
          ) : (
            <button 
              className="bs_ban_button" 
              onClick={handleBanUser} 
              disabled={loading || confirmText !== 'BAN'}
            >
              {loading ? 'Processing...' : 'Ban User'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// # Suspend Modal Component
export const SuspendModal = ({ user, isOpen, onClose, onSuspendComplete }) => {
  // # State management
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  // # Handle suspend user
  const handleSuspendUser = async () => {
    // # Validate confirmation text
    if (confirmText !== 'SUSPEND') {
      setError('Please type SUSPEND to confirm');
      return;
    }

    // # Validate duration
    if (!duration || duration < 1) {
      setError('Please enter a valid duration (minimum 1 day)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // # Call the suspend user function
      const suspendUser = httpsCallable(functions, 'suspendUser');
      const result = await suspendUser({
        uid: user.uid,
        reason: reason || 'Violation of community guidelines',
        durationDays: duration
      });

      // # Handle success
      if (result.data.success) {
        onSuspendComplete(true, result.data.suspensionEnd);
        onClose();
      } else {
        setError(result.data.message || 'Failed to suspend user');
      }
    } catch (error) {
      console.error('Error suspending user:', error);
      setError(error.message || 'An error occurred while suspending the user');
    } finally {
      setLoading(false);
    }
  };

  // # Handle unsuspend user
  const handleUnsuspendUser = async () => {
    setLoading(true);
    setError(null);

    try {
      // # Call the unsuspend user function
      const unsuspendUser = httpsCallable(functions, 'unsuspendUser');
      const result = await unsuspendUser({
        uid: user.uid
      });

      // # Handle success
      if (result.data.success) {
        onSuspendComplete(false);
        onClose();
      } else {
        setError(result.data.message || 'Failed to unsuspend user');
      }
    } catch (error) {
      console.error('Error unsuspending user:', error);
      setError(error.message || 'An error occurred while unsuspending the user');
    } finally {
      setLoading(false);
    }
  };

  // # If modal is not open, don't render anything
  if (!isOpen) return null;

  // # Check if user is already suspended
  const isSuspended = user?.suspends?.isSuspended === true;

  // # Format suspension end date
  const formatSuspensionEnd = () => {
    if (!user?.suspends?.suspensionEnd) return 'Unknown';
    
    try {
      const date = new Date(user.suspends.suspensionEnd);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div className="bs_modal_overlay">
      <div className="bs_modal">
        <div className="bs_modal_header">
          <h2>{isSuspended ? 'Unsuspend User' : 'Suspend User'}</h2>
          <button className="bs_close_button" onClick={onClose}>×</button>
        </div>

        <div className="bs_modal_content">
          {isSuspended ? (
            // # Unsuspend user content
            <div className="bs_unsuspend_content">
              <p>
                This user is currently suspended for the following reason:
              </p>
              <div className="bs_suspend_reason">
                {user.suspends.suspendReason || 'No reason provided'}
              </div>
              <p>
                Suspended on: {new Date(user.suspends.suspendedAt?.toDate?.() || user.suspends.suspendedAt).toLocaleString()}
              </p>
              <p>
                Suspended by: {user.suspends.suspendedBy || 'Admin'}
              </p>
              <p>
                Suspension ends: {formatSuspensionEnd()}
              </p>
              <p className="bs_warning">
                Are you sure you want to unsuspend this user? They will regain access to the platform immediately.
              </p>
            </div>
          ) : (
            // # Suspend user content
            <div className="bs_suspend_content">
              <p className="bs_warning">
                Warning: Suspending a user will temporarily prevent them from accessing the platform.
              </p>
              <div className="bs_form_group">
                <label>Reason for suspension:</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Provide a reason for suspending this user"
                  rows={4}
                />
              </div>
              <div className="bs_form_group">
                <label>Suspension duration (days):</label>
                <input
                  type="number"
                  min="1"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                />
              </div>
              <div className="bs_form_group">
                <label>Type "SUSPEND" to confirm:</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type SUSPEND to confirm"
                />
              </div>
            </div>
          )}

          {error && <div className="bs_error">{error}</div>}
        </div>

        <div className="bs_modal_footer">
          <button className="bs_cancel_button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          {isSuspended ? (
            <button 
              className="bs_unsuspend_button" 
              onClick={handleUnsuspendUser} 
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Unsuspend User'}
            </button>
          ) : (
            <button 
              className="bs_suspend_button" 
              onClick={handleSuspendUser} 
              disabled={loading || confirmText !== 'SUSPEND'}
            >
              {loading ? 'Processing...' : 'Suspend User'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
